import React, { useState } from 'react';
import { useOnboarding } from '../OnboardingContext';

const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function emptyDay() {
  return { closed: false, open: '', close: '' };
}

export default function StepHours({ onNext }) {
  const { state, setField, merge } = useOnboarding();
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState('');
  const [bulkClose, setBulkClose] = useState('');

  // Ensure hours exists for all days (used indirectly via state.hours)
  if (!state.hours) {
    const initial = WEEKDAYS.reduce((acc, d) => {
      acc[d] = emptyDay();
      return acc;
    }, {});
    // Set once on first render; safe because it only runs when missing
    setField('hours', initial);
  }

  const setDay = (day, patch) => {
    const next = { ...(state.hours || {}), [day]: { ...(state.hours?.[day] || emptyDay()), ...patch } };
    setField('hours', next);
  };

  const toggleClosed = (day) => {
    const current = state.hours?.[day] || emptyDay();
    setDay(day, { closed: !current.closed });
  };

  const toggleSelect = (day) => {
    setSelected((s) => (s.includes(day) ? s.filter((x) => x !== day) : [...s, day]));
  };

  const applyBulk = () => {
    if (!selected.length) return;
    const patch = {};
    if (bulkOpen) patch.open = bulkOpen;
    if (bulkClose) patch.close = bulkClose;
    // apply to all selected days
    const next = { ...(state.hours || {}) };
    selected.forEach((d) => {
      next[d] = { ...(next[d] || emptyDay()), ...patch };
    });
    setField('hours', next);
  };

  const quickSelect = (mode) => {
    if (mode === 'mon-fri') setSelected(['monday','tuesday','wednesday','thursday','friday']);
    if (mode === 'all') setSelected(WEEKDAYS.slice());
    if (mode === 'none') setSelected([]);
  };

  const onChangeTime = (day, field, value) => {
    setDay(day, { [field]: value });
  };

  const handleNext = () => {
    // Ensure hours exists for all days before moving on
    const complete = WEEKDAYS.reduce((acc, d) => ({ ...acc, [d]: state.hours?.[d] || emptyDay() }), {});
    merge({ hours: complete });
    onNext();
  };

  return (
    <div style={{ padding: 24 }}>
      <h3>Hours</h3>
      <p>Enter open and close times for each day. Mark a day as closed if you're not open.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '200px 120px 120px 100px 60px', gap: 8, alignItems: 'center' }}>
        <strong>Day</strong>
        <strong>Open</strong>
        <strong>Close</strong>
        <strong>Closed</strong>
        <strong>Select</strong>
        {WEEKDAYS.map((d) => (
          <React.Fragment key={d}>
            <div style={{ padding: '8px 0' }}>{d[0].toUpperCase() + d.slice(1)}</div>
            <div>
              <input
                type="time"
                value={(state.hours && state.hours[d] && state.hours[d].open) || ''}
                onChange={(e) => onChangeTime(d, 'open', e.target.value)}
                disabled={state.hours && state.hours[d] && state.hours[d].closed}
              />
            </div>
            <div>
              <input
                type="time"
                value={(state.hours && state.hours[d] && state.hours[d].close) || ''}
                onChange={(e) => onChangeTime(d, 'close', e.target.value)}
                disabled={state.hours && state.hours[d] && state.hours[d].closed}
              />
            </div>
            <div>
              <label>
                <input
                  type="checkbox"
                  checked={(state.hours && state.hours[d] && state.hours[d].closed) || false}
                  onChange={() => toggleClosed(d)}
                />
              </label>
            </div>
            <div>
              <input type="checkbox" checked={selected.includes(d)} onChange={() => toggleSelect(d)} />
            </div>
          </React.Fragment>
        ))}
      </div>

      <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label>Bulk open <input type="time" value={bulkOpen} onChange={(e) => setBulkOpen(e.target.value)} /></label>
          <label>Bulk close <input type="time" value={bulkClose} onChange={(e) => setBulkClose(e.target.value)} /></label>
          <button onClick={applyBulk}>Apply to selected</button>
          <button onClick={() => quickSelect('mon-fri')}>Select Mon–Fri</button>
          <button onClick={() => quickSelect('all')}>Select All</button>
          <button onClick={() => quickSelect('none')}>Clear Selection</button>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <button onClick={handleNext}>Next</button>
      </div>
    </div>
  );
}
